<template>
    <div class="card usage-card h-100">
        <div class="card-header usage-card__header">
            <h2 class="card-header-title mb-0 text-truncate">{{ creditLabel }}</h2>
            <div class="usage-card__controls">
                <!-- Combined vs breakdown (chart only, and only when there is something to break down) -->
                <div v-if="display === 'chart' && hasBreakdown" class="btn-group btn-group-sm" role="group" aria-label="Chart grouping">
                    <button type="button" class="btn" :class="viewMode === 'combined' ? 'btn-primary' : 'btn-white'"
                        @click="viewMode = 'combined'">Combined</button>
                    <button type="button" class="btn" :class="viewMode === 'breakdown' ? 'btn-primary' : 'btn-white'"
                        @click="viewMode = 'breakdown'">Breakdown</button>
                </div>
                <!-- Chart vs table -->
                <div class="btn-group btn-group-sm" role="group" aria-label="Chart or table">
                    <button type="button" class="btn" :class="display === 'chart' ? 'btn-primary' : 'btn-white'"
                        title="Chart view" aria-label="Chart view" @click="display = 'chart'"><i class="fa fa-chart-column"></i></button>
                    <button type="button" class="btn" :class="display === 'table' ? 'btn-primary' : 'btn-white'"
                        title="Table view" aria-label="Table view" @click="display = 'table'"><i class="fa fa-table-list"></i></button>
                </div>
            </div>
        </div>
        <div class="card-body">
            <!-- Summary metric: icon + everything else in a tight two-line block -->
            <div class="usage-summary d-flex align-items-center">
                <div class="usage-icon" :class="{ 'usage-icon--danger': isOver }">
                    <i class="fa fa-coins"></i>
                </div>
                <div class="flex-grow-1 ms-3 min-w-0">
                    <div class="d-flex align-items-baseline justify-content-between">
                        <div class="usage-summary__metric text-truncate">
                            <span class="usage-metric">{{ formatNumber(item.consumption, { decimals: 1 }) }}</span>
                            <span class="usage-metric__total">/ {{ formatNumber(item.quantity) }}</span>
                            <span v-if="item.unit" class="usage-summary__unit">{{ item.unit }}</span>
                        </div>
                        <span class="usage-summary__pct ms-2"
                            :class="isOver ? 'text-danger' : 'text-primary'">{{ formatNumber(item.consumption_percentage, { decimals: 1 }) }}%</span>
                    </div>
                    <div class="d-flex align-items-center mt-2">
                        <div class="progress usage-progress flex-grow-1">
                            <div class="progress-bar" role="progressbar"
                                :class="isOver ? 'bg-danger' : 'bg-primary'"
                                :style="{ width: barWidth(item.consumption_percentage) + '%' }"
                                :aria-valuenow="item.consumption_percentage" aria-valuemin="0" aria-valuemax="100">
                            </div>
                        </div>
                        <span v-if="overUnderLabel" class="usage-summary__headroom ms-3"
                            :class="isOver ? 'text-danger' : 'text-body-secondary'">{{ overUnderLabel }}</span>
                    </div>
                </div>
            </div>

            <hr />

            <!-- Chart (kept in the DOM and toggled with v-show so it never has to be
                 rebuilt from scratch when returning from the table view) -->
            <div v-show="display === 'chart'" class="chart-wrapper">
                <canvas ref="canvas" class="chart-canvas"></canvas>
            </div>

            <!-- Table -->
            <div v-show="display === 'table'" class="usage-table-wrap">
                <table class="table table-sm mb-0 usage-table">
                    <thead>
                        <tr>
                            <th v-if="showSubColumn">Sub-entitlement</th>
                            <th v-if="showTenantColumn">Tenant</th>
                            <th class="text-end">Consumption</th>
                            <th class="text-end">Count</th>
                            <th class="text-end">% of entitlement</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(row, i) in tableRows" :key="i">
                            <td v-if="showSubColumn" class="usage-table__label">{{ row.sub || '—' }}</td>
                            <td v-if="showTenantColumn" class="usage-table__muted">{{ row.cid || '—' }}</td>
                            <td class="text-end usage-table__num">{{ formatNumber(row.consumption, { decimals: 1 }) }}</td>
                            <td class="text-end usage-table__muted">{{ row.usage != null ? formatNumber(row.usage) : '—' }}</td>
                            <td class="text-end usage-table__num">{{ percentOfEntitlement(row.consumption) }}</td>
                        </tr>
                    </tbody>
                    <tfoot v-if="tableRows.length > 1">
                        <tr>
                            <td :colspan="footColspan">Total</td>
                            <td class="text-end usage-table__num">{{ formatNumber(totalConsumption, { decimals: 1 }) }}</td>
                            <td class="text-end">{{ totalUsage != null ? formatNumber(totalUsage) : '—' }}</td>
                            <td class="text-end usage-table__num">{{ percentOfEntitlement(totalConsumption) }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import {
    Chart,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Legend,
    Tooltip,
} from 'chart.js';
import { formatNumber, formatCompactNumber, formatCreditLabel } from '../../utils/commonFunction';
import { styleDataset, barScales, tooltipStyle, limitLinePlugin, stackTotalPlugin } from '../../utils/chartTheme';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

export default {
    name: 'EntitlementUsageCard',
    props: {
        item: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            viewMode: 'combined', // 'combined' | 'breakdown'
            display: 'chart', // 'chart' | 'table'
            chart: null,
        };
    },
    computed: {
        creditLabel() {
            return formatCreditLabel(this.item.description);
        },
        isOver() {
            return (Number(this.item.consumption) || 0) > (Number(this.item.quantity) || 0);
        },
        // Credits remaining under the entitlement, or the amount over it.
        overUnderLabel() {
            const qty = Number(this.item.quantity) || 0;
            if (!qty) return '';
            const diff = Math.abs(qty - (Number(this.item.consumption) || 0));
            return this.isOver
                ? `${formatNumber(diff, { decimals: 1 })} over`
                : `${formatNumber(diff, { decimals: 1 })} left`;
        },
        subEntitlements() {
            return Array.isArray(this.item.sub_entitlements) ? this.item.sub_entitlements : [];
        },
        subs() {
            return this.subEntitlements.map(s => s.name);
        },
        // One row per (sub-entitlement, tenant); falls back to the sub's own value when
        // there is no tenant breakdown.
        segments() {
            const out = [];
            for (const sub of this.subEntitlements) {
                const tenantUsage = Array.isArray(sub.tenant_usage) ? sub.tenant_usage : [];
                if (tenantUsage.length) {
                    for (const tu of tenantUsage) {
                        out.push({
                            sub: sub.name,
                            cid: tu.cid,
                            consumption: Number(tu.consumption) || 0,
                            usage: tu.usage != null ? Number(tu.usage) : null,
                        });
                    }
                } else {
                    out.push({
                        sub: sub.name,
                        cid: null,
                        consumption: Number(sub.consumption) || 0,
                        usage: sub.usage != null ? Number(sub.usage) : null,
                    });
                }
            }
            return out;
        },
        tenants() {
            const seen = [];
            for (const seg of this.segments) {
                if (seg.cid != null && !seen.includes(seg.cid)) seen.push(seg.cid);
            }
            return seen;
        },
        useTenantSeries() {
            return this.tenants.length > 0;
        },
        // Combined and breakdown only differ once there is more than one sub-entitlement
        // to spread across the x-axis; otherwise it is the same single column.
        hasBreakdown() {
            return this.subs.length > 1;
        },
        effectiveMode() {
            return this.hasBreakdown ? this.viewMode : 'combined';
        },
        showTenantColumn() {
            return this.useTenantSeries;
        },
        showSubColumn() {
            return this.subs.length > 0;
        },
        footColspan() {
            return (this.showSubColumn ? 1 : 0) + (this.showTenantColumn ? 1 : 0) || 1;
        },
        tableRows() {
            if (!this.segments.length) {
                return [{
                    sub: null,
                    cid: null,
                    consumption: Number(this.item.consumption) || 0,
                    usage: this.item.usage != null ? Number(this.item.usage) : null,
                }];
            }
            return this.segments;
        },
        totalConsumption() {
            return this.tableRows.reduce((sum, r) => sum + (Number(r.consumption) || 0), 0);
        },
        totalUsage() {
            const anyKnown = this.tableRows.some(r => r.usage != null);
            if (!anyKnown) return null;
            return this.tableRows.reduce((sum, r) => sum + (Number(r.usage) || 0), 0);
        },
    },
    watch: {
        viewMode() {
            this.$nextTick(() => this.drawChart());
        },
        display(mode) {
            // Keep the chart instance alive across view switches; when the canvas becomes
            // visible again it just needs a resize (a hidden canvas renders at 0×0).
            if (mode === 'chart') {
                this.$nextTick(() => {
                    if (this.chart) this.chart.resize();
                    else this.drawChart();
                });
            }
        },
        item: {
            deep: true,
            handler() {
                this.$nextTick(() => this.drawChart());
            },
        },
    },
    mounted() {
        if (this.display === 'chart') this.$nextTick(() => this.drawChart());
    },
    beforeUnmount() {
        this.destroyChart();
    },
    methods: {
        formatNumber(value, options) {
            return formatNumber(value, options);
        },
        barWidth(percentage) {
            const pct = Number(percentage) || 0;
            return Math.max(0, Math.min(pct, 100));
        },
        percentOfEntitlement(consumption) {
            const qty = Number(this.item.quantity) || 0;
            if (!qty) return '—';
            return formatNumber((Number(consumption) || 0) / qty * 100, { decimals: 1 }) + '%';
        },
        segmentValue(sub, cid) {
            return this.segments.find(s => s.sub === sub && s.cid === cid);
        },
        // Build { labels, datasets, totals } for the requested mode.
        buildData(mode) {
            const combined = mode === 'combined';

            // No sub/tenant detail — a single bar of the credit's own consumption.
            if (!this.segments.length) {
                const value = Number(this.item.consumption) || 0;
                const usage = this.item.usage != null ? Number(this.item.usage) : null;
                return {
                    labels: [this.creditLabel],
                    datasets: [{
                        label: 'Usage',
                        data: [{ key: this.creditLabel, value, usage }],
                        parsing: { xAxisKey: 'key', yAxisKey: 'value' },
                    }],
                    totals: [{ x: 0, value }],
                    countByLabel: { [this.creditLabel]: usage || 0 },
                };
            }

            const series = this.useTenantSeries ? this.tenants : this.subs;
            const seriesFor = (member) => this.useTenantSeries
                ? this.segments.filter(s => s.cid === member)
                : this.segments.filter(s => s.sub === member);

            if (combined) {
                const labels = [this.creditLabel];
                const datasets = series.map(member => {
                    const segs = seriesFor(member);
                    const value = segs.reduce((sum, s) => sum + s.consumption, 0);
                    const usage = segs.reduce((sum, s) => sum + (s.usage || 0), 0);
                    return {
                        label: String(member),
                        data: [{ key: this.creditLabel, value, usage }],
                        parsing: { xAxisKey: 'key', yAxisKey: 'value' },
                    };
                });
                const total = this.segments.reduce((sum, s) => sum + s.consumption, 0);
                const totalUsage = this.segments.reduce((sum, s) => sum + (Number(s.usage) || 0), 0);
                return {
                    labels,
                    datasets,
                    totals: [{ x: 0, value: total }],
                    countByLabel: { [this.creditLabel]: totalUsage },
                };
            }

            // Breakdown: categories are the sub-entitlements; tenant credit series stack
            // within each.
            const labels = this.subs;
            const datasets = series.map(member => {
                const data = this.subs.map(subName => {
                    const seg = this.useTenantSeries
                        ? this.segmentValue(subName, member)
                        : (subName === member ? this.segments.find(s => s.sub === subName) : null);
                    return {
                        key: subName,
                        value: seg ? seg.consumption : 0,
                        usage: seg ? seg.usage : 0,
                    };
                });
                return {
                    label: String(member),
                    data,
                    parsing: { xAxisKey: 'key', yAxisKey: 'value' },
                };
            });
            const totals = this.subs.map((subName, i) => ({
                x: i,
                value: this.segments.filter(s => s.sub === subName).reduce((sum, s) => sum + s.consumption, 0),
            }));
            // Total raw count per sub-entitlement across all tenants (shown in the tooltip).
            const countByLabel = {};
            this.subs.forEach(subName => {
                countByLabel[subName] = this.segments
                    .filter(s => s.sub === subName)
                    .reduce((sum, s) => sum + (Number(s.usage) || 0), 0);
            });

            return { labels, datasets, totals, countByLabel };
        },
        drawChart() {
            this.destroyChart();
            const canvas = this.$refs.canvas;
            if (!canvas) return;

            const { labels, datasets, totals, countByLabel } = this.buildData(this.effectiveMode);
            const styled = datasets.map((d, i) => styleDataset(d, i));

            const scaleCfg = barScales();
            // In the combined view, keep the entitlement line on-screen so you can always
            // see how the total consumption is tracking against it (with a little headroom
            // for the line's label). Over-limit usage still scales past it.
            const quantity = Number(this.item.quantity) || 0;
            if (this.effectiveMode === 'combined' && quantity > 0) {
                scaleCfg.y.suggestedMax = quantity * 1.1;
            }

            const counts = countByLabel || {};
            // Raw counts are only meaningful when a bar represents a single sub-entitlement
            // (one metering rate). The combined view sums a tenant's usage across different
            // sub-entitlements that consume credits at different rates, so a raw count there
            // would be apples-to-oranges — hide it. (A credit with 0–1 sub-entitlements is a
            // single rate even though it renders as one "combined" bar.)
            const showCounts = this.effectiveMode !== 'combined' || this.subs.length <= 1;

            this.chart = new Chart(canvas, {
                type: 'bar',
                data: { labels, datasets: styled },
                options: {
                    plugins: {
                        legend: {
                            display: styled.length > 1,
                            position: 'bottom',
                            labels: {
                                usePointStyle: true,
                                pointStyle: 'circle',
                                boxWidth: 8,
                                boxHeight: 8,
                                padding: 16,
                                color: '#6E84A3',
                                font: { size: 11 },
                            },
                        },
                        tooltip: tooltipStyle({
                            afterLabel: (context) => {
                                if (!showCounts) return undefined;
                                const usage = context.raw && context.raw.usage != null ? context.raw.usage : 0;
                                return 'Count: ' + usage.toLocaleString();
                            },
                            // Total raw count for the whole stack (the bar's sub-entitlement
                            // across all tenants). Omitted for aggregated combined bars.
                            footer: (items) => {
                                if (!showCounts || !items || !items.length) return undefined;
                                const total = counts[items[0].label];
                                if (!total) return undefined;
                                return 'Total count: ' + formatCompactNumber(total);
                            },
                        }),
                        limitLine: { label: 'Current entitlement', limit: this.item.quantity },
                        stackTotal: { totals },
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 22 } },
                    scales: scaleCfg,
                },
                plugins: [limitLinePlugin, stackTotalPlugin],
            });
        },
        destroyChart() {
            if (this.chart && typeof this.chart.destroy === 'function') {
                this.chart.destroy();
            }
            this.chart = null;
        },
    },
};
</script>

<style scoped>
.chart-wrapper {
    position: relative;
    width: 100%;
    height: 300px;
}

.usage-card {
    border: 1px solid rgba(18, 38, 63, 0.06);
}

/* Header: title on the left, controls on the right; controls wrap neatly under the
   title on narrow cards instead of stacking mid-row. */
.usage-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
}

.usage-card__header .card-header-title {
    min-width: 0;
    flex: 1 1 auto;
}

.usage-card__controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.usage-metric {
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
}

.usage-metric__total {
    font-size: 1.05rem;
    font-weight: 500;
    color: #95aac9;
    margin-left: 0.25rem;
}

.min-w-0 {
    min-width: 0;
}

.usage-summary__metric {
    min-width: 0;
}

.usage-summary__unit {
    font-size: 0.8rem;
    color: #95aac9;
    margin-left: 0.35rem;
}

.usage-summary__pct {
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
}

.usage-summary__headroom {
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
}

.usage-progress {
    height: 8px;
    border-radius: 999px;
    background-color: rgba(44, 123, 229, 0.1);
}

.usage-progress .progress-bar {
    border-radius: 999px;
}

.usage-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    flex-shrink: 0;
    border-radius: 50%;
    color: #2c7be5;
    background: rgba(44, 123, 229, 0.1);
    font-size: 1rem;
}

.usage-icon--danger {
    color: #e63757;
    background: rgba(230, 55, 87, 0.1);
}

/* Table view — aligned to the chart height so toggling doesn't jump the card size. */
.usage-table-wrap {
    height: 322px;
    overflow-y: auto;
}

.usage-table {
    font-size: 0.85rem;
}

.usage-table th {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #95aac9;
    border-top: 0;
    border-bottom: 1px solid rgba(18, 38, 63, 0.08);
    padding: 0.5rem 0.75rem;
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--bs-card-bg, #fff);
    white-space: nowrap;
}

.usage-table td {
    vertical-align: middle;
    padding: 0.5rem 0.75rem;
    border-top: 0;
    border-bottom: 1px solid rgba(18, 38, 63, 0.05);
}

.usage-table tbody tr:hover td {
    background: rgba(44, 123, 229, 0.04);
}

.usage-table__label {
    font-weight: 500;
    color: #12263f;
}

.usage-table__muted {
    color: #6e84a3;
}

.usage-table__num {
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    color: #12263f;
}

.usage-table tfoot td {
    border-top: 2px solid rgba(18, 38, 63, 0.1);
    border-bottom: 0;
    font-weight: 700;
    color: #12263f;
    position: sticky;
    bottom: 0;
    background: var(--bs-card-bg, #fff);
}
</style>
